import Input from "components/atom/Input.tsx";
import {ErrorMessage, useFormikContext} from "formik";
import {useTranslation} from "react-i18next";
import {Fragment, useCallback, useEffect, useMemo, useRef} from "react";
import Select from "components/atom/Select.tsx";
import {CategoryType, UserType} from "globals/types/models.ts";
import {useAppSelector} from "config/store";
import FormImage from "components/form/FormImage.tsx";
import {EventDateFormValuesType, EventFormValuesType, EventImageFormValuesType} from "utils/form.ts";
import FormLabel from "components/form/FormLabel.tsx";
import dayjs from 'dayjs'
import {Map, Marker} from "maplibre-gl";
import {useMount} from "react-use";
import EventDateItem from "components/layouts/form/event/EventDateItem.tsx";
import markerPng from 'assets/images/marker.png'
import FormInputPhone from "components/form/FormInputPhone.tsx";
import {usePlacesWidget} from "react-google-autocomplete";
import {CA_PROVINCES, LANGS} from "globals/constants.ts";
import {OSMStyle} from "utils/data/map.ts";
import {GooglePlaceType} from "globals/types/globals.ts";
import {getLatitude, getLongitude} from "utils/global.ts";

const GOOGLE_MAPS_API_KEY = process.env.GOOGLE_MAPS_API_KEY as string

function EventForm(props: { disabled?: boolean, isAdmin?: boolean }) {
    const {t} = useTranslation()
    const categories: CategoryType[] = useAppSelector(state => state.global.categories)
    const currentUser: UserType | null = useAppSelector(state => state.auth.user)
    const currentLang = useAppSelector(state => state.global.currentLang)
    const {values, setFieldValue, errors} = useFormikContext<EventFormValuesType>()

    const mapRef = useRef<Map | null>(null)
    const markerRef = useRef<Marker | null>(null)
    const mapDivRef = useRef<HTMLDivElement | null>(null)


    useMount(() => {
        if (!mapDivRef.current) {
            return
        }

        if (mapRef.current) {
            mapRef.current?.remove()
            mapRef.current = null
        }

        mapRef.current = new Map({
            container: mapDivRef.current,
            attributionControl: false,
            dragPan: false,
            dragRotate: false,
            scrollZoom: false,
            doubleClickZoom: false,
            touchZoomRotate: false,
            //@ts-expect-error style is not in the types
            style: OSMStyle,
            center: {
                lat: 46,
                lng: -73
            },
            zoom: 6,
        });

        return () => {
            mapRef.current?.remove()
            mapRef.current = null
        }
    })

    useEffect(() => {
        if (!mapRef.current) return

        const valueLat = getLatitude(values.latitude)
        const valueLng = getLongitude(values.longitude)

        if (markerRef.current) {
            if (!valueLat || !valueLng) {
                markerRef.current?.remove()
                return
            }

            mapRef.current?.setZoom(15)
            mapRef.current?.setCenter([valueLng, valueLat])
            return
        }

        if (!valueLat || !valueLng) {
            return
        }

        const markerElement = document.createElement('div')
        markerElement.className = 'g-icon-marker'
        markerElement.innerHTML = `<img class="block min-w-[60px] w-[60px] h-[60px]" src="${markerPng}" alt="marker"/>`

        markerRef.current = new Marker({
            element: markerElement
        })
            .setLngLat([valueLng, valueLat])
            .addTo(mapRef.current);


        mapRef.current?.setZoom(15)
        mapRef.current?.setCenter([valueLng, valueLat])

        return () => {
            markerRef.current?.remove();
            markerRef.current = null
        }
    }, [mapRef, values.latitude, values.longitude])


    const onPlaceSelected = useCallback((place: GooglePlaceType | undefined) => {
        if (!place) return
        let city = ''
        let zipcode = ''
        let state = ''
        const components = place.address_components || []
        for (const component of components) {
            if (component.types.includes('locality')) {
                city = component.long_name
            } else if (component.types.includes('postal_code')) {
                zipcode = component.long_name
            } else if (component.types.includes('administrative_area_level_1')) {
                state = component.short_name
            }
        }

        setFieldValue('address', place.formatted_address)
        setFieldValue('city', city)
        setFieldValue('zipcode', zipcode)
        setFieldValue('state', state)

        setFieldValue('latitude', place.geometry?.location?.lat())
        setFieldValue('longitude', place.geometry?.location?.lng())
    }, [setFieldValue])

    const {ref: autocompleteInputRef} = usePlacesWidget({
        apiKey: GOOGLE_MAPS_API_KEY,
        onPlaceSelected: onPlaceSelected,
        options: {
            types: ['geocode'],
            componentRestrictions: {country: 'ca'}
        },
        language: currentLang,
        inputAutocompleteValue: values.address ? values.address : ''
    });

    useEffect(() => {
        //autofill input with address
        if (!autocompleteInputRef.current) return
        (autocompleteInputRef.current as HTMLInputElement).value = values.address ? values.address : ''
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autocompleteInputRef.current])

    const onUploadImages = useCallback((files: { imagePath: string, imageUrl: string }[]) => {
        const images: EventImageFormValuesType[] = [...values.images]
        for (const file of files) {
            images.push({
                imagePath: file.imagePath,
                imageUrl: file.imageUrl,
                orderIdx: images.length,
                formats: null,
            })
        }
        setFieldValue('images', images)
    }, [setFieldValue, values.images])


    const onDeleteImage = useCallback((idx: number) => {
        const images: EventImageFormValuesType[] = [...values.images]
        images.splice(idx, 1)
        setFieldValue('images', images)
    }, [setFieldValue, values.images])


    /* const onDeleteDate = useCallback((idx: number) => {
         if (values.dates.length <= 1) return
         const dates: EventDateFormValuesType[] = [...values.dates]
         dates.splice(idx, 1)
         setFieldValue('dates', dates)
     }, [values])

     const onAddDate = useCallback(() => {
         const dates: EventDateFormValuesType[] = [...values.dates]
         dates.push({startDate: null, endDate: null})
         setFieldValue('dates', dates)
     }, [values]) */


    const recurringDates = useMemo(() => {
        if (values.dates.length === 0) return []
        if (!values.recurring) return []
        if (values.recurring === 'none') return []

        const dates = [...values.dates]
        dates.splice(0, 1)//remove first one
        return dates
    }, [values.recurring, values.dates])

    const onRecurringChange = useCallback((e: { target: { value: string } }) => {
        if (!currentUser) return
        //new plan dont check that
        // if (currentUser.planType !== USER_PLAN_TYPES.FREE_COMPLETE && currentUser.planType !== USER_PLAN_TYPES.COMPLETE) return//must be in complete or free complete plan type to use that
        if (values.dates.length === 0) return
        const initDate = values.dates[0]
        const length = values.dates.length
        //we need to make sure we dont delete dates
        //let say event was created 2 months ago, we got 10 dates, and recurring is selected to weekly with first date... we need to create the same amount of dates as the first one
        const newDates = [
            {...initDate}//make sure to copy
        ];
        const recurring = e.target.value

        setFieldValue('recurring', recurring)

        if (recurring === 'daily') {
            //8 minimum dates for daily
            const dailyArr = Array.from({length: length > 8 ? length : 8}, (_, idx) => idx + 1);

            //make sure we got atleast a week of dates so search get new dates
            dailyArr.map(item => {
                const newDate: EventDateFormValuesType = {
                    startDate: dayjs(initDate.startDate).add(item, 'days').format('YYYY-MM-DD HH:mm:ss'),
                    endDate: dayjs(initDate.endDate).add(item, 'days').format('YYYY-MM-DD HH:mm:ss')
                }
                newDates.push(newDate)
            })
        } else if (recurring === 'monthly') {
            //3 minimum dates for monthly
            const monthlyArr = Array.from({length: length > 3 ? length : 3}, (_, idx) => idx + 1);

            monthlyArr.map(item => {
                const newDate: EventDateFormValuesType = {
                    startDate: dayjs(initDate.startDate).add(item, 'months').format('YYYY-MM-DD HH:mm:ss'),
                    endDate: dayjs(initDate.endDate).add(item, 'months').format('YYYY-MM-DD HH:mm:ss')
                }
                newDates.push(newDate)
            })
        } else if (recurring === 'weekly') {
            //3 minimum dates for weekly
            const weeklyArr = Array.from({length: length > 3 ? length : 3}, (_, idx) => idx + 1);

            weeklyArr.map(item => {
                const newDate: EventDateFormValuesType = {
                    startDate: dayjs(initDate.startDate).add(item, 'weeks').format('YYYY-MM-DD HH:mm:ss'),
                    endDate: dayjs(initDate.endDate).add(item, 'weeks').format('YYYY-MM-DD HH:mm:ss')
                }
                newDates.push(newDate)
            })
        } else if (recurring === 'bi-weekly') {
            //3 minimum dates for bi-weekly
            const weeklyArr = Array.from({length: length > 3 ? length : 3}, (_, idx) => idx + 1);

            weeklyArr.map(item => {
                //if item is 1 = 2 weeks, 2 = 4 weeks, 3 = 6 weeks
                const newDate: EventDateFormValuesType = {
                    startDate: dayjs(initDate.startDate).add(item * 2, 'weeks').format('YYYY-MM-DD HH:mm:ss'),
                    endDate: dayjs(initDate.endDate).add(item * 2, 'weeks').format('YYYY-MM-DD HH:mm:ss')
                }
                newDates.push(newDate)
            })
        }

        setFieldValue('dates', newDates)
    }, [currentUser, setFieldValue, values.dates])

    const onFirstDateChanged = useCallback(() => {
        if (values.recurring && values.recurring !== 'none') {
            onRecurringChange({target: {value: values.recurring}})//fake event style
        }
    }, [onRecurringChange, values])

    const recurringDisabled = useMemo(() => {
        if (!currentUser) return true
        if (props.disabled) return true
        if (values.dates && !values.dates[0].startDate) return true
        return false
        //todo new trial dont check that
        //return currentUser.planType !== USER_PLAN_TYPES.FREE_COMPLETE && currentUser.planType !== USER_PLAN_TYPES.COMPLETE
    }, [currentUser, props.disabled, values.dates])

    return (
        <div>
            <Input disabled={props.disabled} name="titleFr" type="text" label={t('forms.titleFr')}/>
            <Input disabled={props.disabled} name="descriptionFr" type="textarea" label={t('forms.descriptionFr')}/>


            <div className="grid grid-cols-1 gap-3 pb-3 md:grid-cols-2">

                <div>
                    <div className="c__input">
                        <FormLabel>
                            {t('event.location')}
                        </FormLabel>
                        <input disabled={props.disabled} ref={autocompleteInputRef} type="text"
                               className="input w-full"/>
                        <ErrorMessage name="address" component="p" className="error"/>
                    </div>

                    {values.address && (
                        <div className="">
                            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
                                <Input disabled name="address" type="text" label={t('forms.streetAddress')}/>
                                <Input disabled name="city" type="text" label={t('forms.city')}/>
                            </div>
                            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-3">
                                <Input disabled name="zipcode" type="text" label={t('forms.zipcode')}/>

                                <Select disabled name="state" type="text" label={t('forms.state')}>
                                    {Object.keys(CA_PROVINCES).map((stateCode) => (
                                        <option key={stateCode} value={stateCode}>
                                            {CA_PROVINCES[stateCode]}
                                        </option>
                                    ))}
                                </Select>

                                <Select disabled name="country" type="text" label={t('forms.country')}>
                                    <option value="CA">Canada</option>
                                </Select>
                            </div>
                            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
                                <Input disabled={!props.isAdmin} name="latitude" type="text"
                                       label={t('forms.latitude')}/>
                                <Input disabled={!props.isAdmin} name="longitude" type="text"
                                       label={t('forms.longitude')}/>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-start justify-center">
                    <div className="w-full flex-1">
                        <div className="h-[200px] w-full overflow-hidden  rounded-md md:h-full md:min-h-[200px]"
                             ref={mapDivRef}/>
                    </div>
                    <p className="pt-2 text-xs">
                        {t('event.locationDescription')}

                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
                <Input disabled={props.disabled} name="price" type="text" placeholder={t('forms.placeholders.price')}
                       label={t('forms.pricing')}/>

                <Select disabled={props.disabled} name="categoryId"
                        label={t('forms.category')}>
                    <option value="">
                        {t('event.noCategory')}
                    </option>
                    {categories.map(category => {
                        const categoryTitle = currentLang === LANGS.FR ? category.titleFr : category.titleEn ? category.titleEn : category.titleFr
                        return (
                            <option key={category.id} value={category.id}>
                                {categoryTitle}
                            </option>
                        )
                    })}
                </Select>
            </div>

            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
                <Input disabled={props.disabled} name="website" placeholder={t('forms.placeholders.url')} type="text"
                       label={t('forms.website')}/>

                <FormInputPhone
                    disabled={props.disabled}
                    label={t('forms.phoneNumber')}
                    value={values.phoneNumber || ''}
                    onChange={({clean}) => setFieldValue('phoneNumber', clean)}
                    name="phoneNumber"
                    error={errors.phoneNumber}
                    type="text"/>
            </div>

            <Input disabled={props.disabled} name="externalLink" placeholder={t('forms.placeholders.url')} type="text"
                   label={t('forms.externalLink')}/>

            <div className="c__input">
                <div className="mt-1 space-y-2">
                    {values.dates.length > 0 && (
                        <EventDateItem
                            disabled={props.disabled}
                            date={values.dates[0]}
                            idx={0}
                            onChanged={onFirstDateChanged}
                        >

                            {/*}
                            {idx === 0 ? (
                                <div className="h-10 w-10 min-w-10"/>
                            ) : (
                                <button
                                    type="button"
                                    className="flex h-10 w-10 min-w-10 flex-col items-center justify-center rounded-full border border-black hover:bg-gray-50"
                                    onClick={() => onDeleteDate(idx)}>
                                    <Icon name="x" className="block h-4 w-4 min-w-4"/>
                                </button>
                            )} */}

                            <Select name="recurring" label={t('forms.recurring')}
                                    formikField={false}
                                    value={values.recurring || ''}
                                    onChange={onRecurringChange}
                                    disabled={recurringDisabled}>
                                <option value="" disabled>
                                    {t('forms.selectRecurring')}
                                </option>
                                <option value="none">{t('noneFeminine')}</option>
                                <option value="weekly">
                                    {t('event.recurring.weeklyDescription')}
                                </option>
                                <option value="bi-weekly">
                                    {t('event.recurring.biWeeklyDescription')}
                                </option>
                                <option value="monthly">
                                    {t('event.recurring.monthlyDescription')}
                                </option>
                                {/* We removed daily  */}
                            </Select>
                        </EventDateItem>
                    )}

                    {recurringDates.length > 0 && (
                        <Fragment>
                            {recurringDates.map((date, idx) => {
                                if (idx >= 3) return null//dont show all dates so it doesnt take too much space
                                return (
                                    <p className="text-xs" key={idx}>
                                        {t('event.recurring.dateDescription', {date: dayjs(date.startDate).format('LL HH:mm')})}
                                    </p>
                                )
                            })}
                            <p className="text-xs">
                                {t('andMore')}
                            </p>
                        </Fragment>
                    )}


                    {/* <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                         <div className="flex flex-col items-center justify-center">
                            <Button small style="text" className="mt-4" onClick={onAddDate}>
                                Ajouter une date
                            </Button>
                        </div>

                    </div>
                    */}
                </div>
            </div>
            <FormImage disabled={props.disabled} values={values.images} uploadFolder="events" onUpload={onUploadImages}
                       onDelete={onDeleteImage}/>
        </div>
    )
}

export default EventForm
